// Per-customer override editor: change ANY plan attribute for one customer —
// prices included — without forking a whole plan.
//
// This replaces an earlier dialog that only exposed the two rate limits, which
// left no way at all to agree a special price with a customer. Every canonical
// plan field is editable here; the server validates the keys, so an unknown
// field is rejected rather than silently ignored.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { adminApi } from '../api/adminClient';
import type { EffectiveLimits, PlanVersionSummary } from '../api/types';
import { useAsync } from '../lib/hooks';
import { ByteInput } from '../components/ByteInput';
import { Field } from '../components/Field';
import { Badge, Button } from '../components/ui';
import { IconClose } from '../components/Icons';

/** How each field is entered. Money and byte counts are plain numbers here —
 *  the operator types the exact contractual figure rather than a rounded unit. */
type Kind = 'int' | 'money' | 'text' | 'list' | 'bytes';

const FIELDS: { key: string; kind: Kind; group: 'price' | 'limit' | 'service' }[] = [
  { key: 'priceWriteEurPerGb', kind: 'money', group: 'price' },
  { key: 'priceStorageEurPerGbYear', kind: 'money', group: 'price' },
  { key: 'priceReadEurPerReadout', kind: 'money', group: 'price' },
  { key: 'priceReadFreeLifetimeCount', kind: 'int', group: 'price' },
  { key: 'minBillableObjectBytes', kind: 'bytes', group: 'price' },
  { key: 'minBillableReadBytes', kind: 'bytes', group: 'price' },

  { key: 'purchasedStorageBytes', kind: 'bytes', group: 'limit' },
  { key: 'rateLimitWriteBytesPerDay', kind: 'bytes', group: 'limit' },
  { key: 'rateLimitReadBytesPerDay', kind: 'bytes', group: 'limit' },
  { key: 'minStoragePerArchiveBytes', kind: 'bytes', group: 'limit' },

  { key: 'writeSlaHours', kind: 'int', group: 'service' },
  { key: 'verificationLevel', kind: 'text', group: 'service' },
  { key: 'verificationFrequency', kind: 'text', group: 'service' },
  { key: 'retrievalOptions', kind: 'list', group: 'service' },
  { key: 'writeTriggers', kind: 'list', group: 'service' },
  { key: 'physicalPackaging', kind: 'text', group: 'service' },
  { key: 'ttfbReadingHours', kind: 'int', group: 'service' },
  { key: 'ttfbSiteId', kind: 'text', group: 'service' },
  { key: 'eccProfile', kind: 'text', group: 'service' },
  { key: 'storageLocationSiteId', kind: 'text', group: 'service' },
  { key: 'mediumRedundancyPercent', kind: 'money', group: 'service' },
];

function toInput(v: unknown, kind: Kind): string {
  if (v === null || v === undefined) return '';
  if (kind === 'list') return Array.isArray(v) ? v.join(', ') : String(v);
  return String(v);
}

function fromInput(raw: string, kind: Kind): unknown {
  if (kind === 'list') return raw.split(',').map((s) => s.trim()).filter(Boolean);
  if (kind === 'int' || kind === 'money' || kind === 'bytes') return Number(raw);
  return raw;
}

export function OverrideEditor({
  tenantId,
  tenantName,
  limits,
  onClose,
  onSaved,
}: {
  tenantId: string;
  tenantName: string;
  /** Current resolved values, so each field starts from what actually applies. */
  limits: EffectiveLimits | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const versions = useAsync(() => adminApi.adminAllPlanVersions(), []);
  const subs = useAsync(() => adminApi.adminSubscriptions(), []);

  const current = useMemo(
    () => subs.data?.items.find((s) => s.tenantId === tenantId) ?? null,
    [subs.data, tenantId],
  );
  const [planVersionId, setPlanVersionId] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const effectivePlanVersion = planVersionId || current?.planVersionId || '';

  // A field shows the resolved value; only fields the operator actually edits
  // are sent, so untouched ones keep falling back to the plan instead of being
  // frozen into overrides.
  const valueFor = (key: string, kind: Kind): string =>
    touched[key] ? (values[key] ?? '') : toInput(limits?.fields?.[key]?.value, kind);

  const sourceOf = (key: string) => limits?.fields?.[key]?.source;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectivePlanVersion) {
      setError(t('admin.override.needPlan'));
      return;
    }
    const overrides: Record<string, unknown> = { ...(current?.overrides ?? {}) };
    for (const f of FIELDS) {
      if (!touched[f.key]) continue;
      const raw = values[f.key] ?? '';
      if (raw.trim() === '') {
        delete overrides[f.key]; // cleared -> fall back to the plan value
        continue;
      }
      overrides[f.key] = fromInput(raw, f.kind);
    }
    setBusy(true);
    setError(undefined);
    try {
      await adminApi.adminAssignSubscription(tenantId, effectivePlanVersion, overrides);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  const renderGroup = (group: 'price' | 'limit' | 'service') => (
    <>
      <h3 style={{ margin: '16px 0 4px', fontSize: 14 }}>{t(`admin.override.group.${group}`)}</h3>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        {FIELDS.filter((f) => f.group === group).map((f) => (
          <Field
            key={f.key}
            label={t(`admin.fields.${f.key}`, f.key)}
            hint={f.kind === 'list' ? t('admin.plans.listHint') : undefined}
          >
            {(p) =>
              // The byte editor brings its own row plus a resolved-size line,
              // so it is not nested in the inline row used by plain inputs.
              f.kind === 'bytes' ? (
                <div>
                  <ByteInput
                    fieldProps={p}
                    value={valueFor(f.key, f.kind)}
                    onChange={(bytes) => {
                      setTouched((s) => ({ ...s, [f.key]: true }));
                      setValues((s) => ({ ...s, [f.key]: bytes }));
                    }}
                  />
                  {sourceOf(f.key) === 'override' ? (
                    <Badge tone="accent">{t('admin.source.override')}</Badge>
                  ) : null}
                </div>
              ) : (
                <span className="row" style={{ gap: 6, alignItems: 'center' }}>
                  <input
                    className="input"
                    {...p}
                    type={f.kind === 'int' || f.kind === 'money' ? 'number' : 'text'}
                    step={f.kind === 'money' ? 'any' : undefined}
                    value={valueFor(f.key, f.kind)}
                    onChange={(e) => {
                      setTouched((s) => ({ ...s, [f.key]: true }));
                      setValues((s) => ({ ...s, [f.key]: e.target.value }));
                    }}
                  />
                  {sourceOf(f.key) === 'override' ? (
                    <Badge tone="accent">{t('admin.source.override')}</Badge>
                  ) : null}
                </span>
              )
            }
          </Field>
        ))}
      </div>
    </>
  );

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="dialog" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="ov-title" style={{ maxWidth: 780 }}>
        <div className="row between">
          <h2 className="dialog__title" id="ov-title">
            {t('admin.override.title', { name: tenantName })}
          </h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
            <IconClose width={16} height={16} />
          </button>
        </div>

        <div style={{ marginTop: 12, maxHeight: '62vh', overflowY: 'auto' }}>
          <Field label={t('admin.assign.planLabel')} error={error}>
            {(p) => (
              <select
                className="select"
                {...p}
                value={effectivePlanVersion}
                onChange={(e) => setPlanVersionId(e.target.value)}
              >
                <option value="">{t('admin.assign.planPlaceholder')}</option>
                {(versions.data?.items ?? []).map((v: PlanVersionSummary) => (
                  <option key={v.id} value={v.id}>
                    {v.planName} (v{v.version})
                  </option>
                ))}
              </select>
            )}
          </Field>

          <p className="muted" style={{ fontSize: 13, margin: '4px 0 0' }}>
            {t('admin.override.hint')}
          </p>

          {renderGroup('price')}
          {renderGroup('limit')}
          {renderGroup('service')}
        </div>

        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
